import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Grid, Checkbox, Tab, Tabs, Typography } from '@mui/material';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { useSelector } from 'react-redux';

import {
  fetchPermissionCategories,
  PermissionCategory,
  PermissionArea as IPermissionArea,
  Permission,
  PermissionType,
} from 'services/permissions';
import { TabPanel } from 'ui/components/TabPanel';
import { activeUserHasPermission } from 'services/user/redux';
import { useFeatureAccess } from 'helpers/serviceTierHooks';

import { PermissionCategoryState, PermissionsTabProps } from './types';
import { usePermissionsTabStyle } from './styled';
import { PermissionArea } from './components';
import {
  addCategoryPermissions,
  getPermissionCategoryState,
  removeCategoryPermissions,
} from './helpers';
import { FeatureAccessKey } from 'types/featureAccess';

// Local type for feature access configuration
type FeatureAccessConfig = Record<FeatureAccessKey, boolean>;

// Pre-compile regex patterns for better performance
const createReportPatterns = (permissions: string[]) =>
  permissions.map(
    (reportName) =>
      new RegExp(
        reportName
          .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          .replace(/\s+/g, '\\s+'),
        'i'
      )
  );

const SALES_REPORT_PATTERNS = createReportPatterns([
  'customer history',
  'item price list',
  'margin',
  'packing list',
  'pick',
  'sales order',
  'sales order summary',
  'shipping invoice',
  'total item sales',
]);

const PURCHASING_REPORT_PATTERNS = createReportPatterns([
  'purchase order',
  'purchase order summary',
  'receiving list',
  'reorder',
  'vendor fulfilment history',
  'bill of lading',
]);

// Helper function to check area access
const shouldHideArea = (
  areaName: string,
  featureAccess: FeatureAccessConfig
): boolean => {
  // Payment-related areas
  if (
    !featureAccess.canAccessPaymentTerms &&
    (areaName === 'payment terms' ||
      areaName.includes('payment term') ||
      areaName === 'payment types' ||
      areaName.includes('payment type'))
  ) {
    return true;
  }

  // Tax rates
  if (
    (areaName === 'tax rates' || areaName.includes('tax rate')) &&
    !featureAccess.canAccessTaxRates
  ) {
    return true;
  }

  // Representatives and carriers
  if (
    areaName.includes('representative') &&
    !featureAccess.canAccessRepresentatives
  ) {
    return true;
  }
  if (areaName.includes('carrier') && !featureAccess.canAccessCarriers) {
    return true;
  }

  // Hide Sales Orders, Picking, Pricing Rules for Essentials users
  if (
    !featureAccess.canAccessSales &&
    ['sales order', 'picking', 'pricing rule', 'pricing rules'].some((s) =>
      areaName.includes(s)
    )
  ) {
    return true;
  }

  // Shipping-related areas
  if (areaName.includes('shipping') && !featureAccess.canAccessCarriers) {
    return true;
  }

  // Vendor-related areas
  if (
    (areaName.includes('vendor') ||
      areaName === 'vendor items' ||
      areaName === 'vendors') &&
    !featureAccess.canAccessPurchasing
  ) {
    return true;
  }

  // Multi-currency
  if (
    (areaName.includes('multi-currency') ||
      areaName.includes('multicurrency')) &&
    !featureAccess.canAccessSales &&
    !featureAccess.canAccessPurchasing
  ) {
    return true;
  }

  // AI Insights
  if (
    (areaName.includes('ai insights') || areaName.includes('ai-insights')) &&
    !featureAccess.canAccessAiInsights
  ) {
    return true;
  }

  return false;
};

// Filter function for permission categories
const filterPermissionCategories = (
  categories: PermissionCategory[],
  featureAccess: FeatureAccessConfig
): PermissionCategory[] => {
  return categories
    .filter((category) => {
      if (!category?.name) return true;

      const categoryName = category.name.toLowerCase();

      // Keep Sales category only if user has sales OR customer access; otherwise hide it
      if (
        categoryName === 'sales' &&
        !featureAccess.canAccessSales &&
        !featureAccess.canAccessCustomers
      ) {
        return false;
      }

      if (categoryName === 'purchasing' && !featureAccess.canAccessPurchasing) {
        return false;
      }

      return true;
    })
    .map((category) => filterPermissionAreas(category, featureAccess));
};

// Filter function for permission areas within categories
const filterPermissionAreas = (
  category: PermissionCategory,
  featureAccess: FeatureAccessConfig
): PermissionCategory => {
  if (!category.permissionAreas) return category;

  const filteredPermissionAreas = category.permissionAreas
    .filter((area) => {
      if (!area?.name) return true;

      const areaName = area.name.toLowerCase();

      // Hide Sales-related areas for Essentials users (except Customers)
      if (
        !featureAccess.canAccessSales &&
        featureAccess.canAccessCustomers &&
        category.name?.toLowerCase() === 'sales'
      ) {
        // Only show Customer-related areas in Sales category for Essentials
        return areaName.includes('customer');
      }

      return !shouldHideArea(areaName, featureAccess);
    })
    .map((area) => filterPermissionsInArea(area, featureAccess));

  return {
    ...category,
    permissionAreas: filteredPermissionAreas,
  };
};

// Filter function for permissions within specific areas
const filterPermissionsInArea = (
  area: IPermissionArea,
  featureAccess: FeatureAccessConfig
): IPermissionArea => {
  if (!area.permissions) return area;

  // Filter permissions within the Reports area
  if (area.name === 'Reports') {
    const filteredPermissions = area.permissions.filter(
      (permission: Permission) => {
        if (!permission?.name) return true;

        const normalizedName = permission.name.toLowerCase();

        // Hide sales report permissions
        if (
          !featureAccess.canAccessSales &&
          SALES_REPORT_PATTERNS.some((pattern) =>
            pattern.test(permission.name!)
          )
        ) {
          return false;
        }

        // Show Reorder report if user has specific access, even without general purchasing access
        if (
          normalizedName.includes('reorder') &&
          featureAccess.canViewReorderReport
        ) {
          return true;
        }

        // Hide purchasing report permissions
        if (
          !featureAccess.canAccessPurchasing &&
          PURCHASING_REPORT_PATTERNS.some((pattern) =>
            pattern.test(permission.name!)
          )
        ) {
          return false;
        }

        // Hide Vendor Fulfillment History specifically for essentials users
        if (
          normalizedName.includes('vendor fulfilment history') &&
          !featureAccess.canViewVendorFulfillmentHistoryReport
        ) {
          return false;
        }

        return true;
      }
    );

    return {
      ...area,
      permissions: filteredPermissions,
    };
  }

  // Filter vendor-related permissions for essentials users in any area
  const filteredPermissions = area.permissions.filter(
    (permission: Permission) => {
      if (!permission?.name) return true;

      const permissionName = permission.name.toLowerCase();

      // Hide vendor items permissions for essentials users
      if (
        permissionName.includes('vendor') &&
        !featureAccess.canAccessPurchasing
      ) {
        return false;
      }

      // Hide shipping permissions for essentials users
      if (
        permissionName.includes('shipping') &&
        !featureAccess.canAccessCarriers
      ) {
        return false;
      }

      return true;
    }
  );

  return {
    ...area,
    permissions: filteredPermissions,
  };
};

const PermissionsTab: React.FC<PermissionsTabProps> = (props) => {
  const { permissionGroup, setPermissionGroup } = props;
  const featureAccess = useFeatureAccess();

  const [activeTab, setActiveTab] = useState(0);
  const [rawPermissionCategories, setRawPermissionCategories] = useState<
    PermissionCategory[]
  >([]);

  const canEditPermissions =
    permissionGroup.id && permissionGroup.id > 0
      ? [PermissionType.PermissionEdit]
      : [PermissionType.PermissionCreate];

  const canEdit = useSelector(activeUserHasPermission(canEditPermissions));

  // Memoize the filtered categories to avoid expensive computation on every feature flag change
  const permissionCategories = useMemo(() => {
    if (rawPermissionCategories.length === 0) return [];

    return filterPermissionCategories(rawPermissionCategories, featureAccess);
  }, [
    rawPermissionCategories,
    featureAccess.canAccessSales,
    featureAccess.canAccessCustomers,
    featureAccess.canAccessPurchasing,
    featureAccess.canAccessPaymentTerms,
    featureAccess.canAccessRepresentatives,
    featureAccess.canAccessTaxRates,
    featureAccess.canAccessCarriers,
    featureAccess.canAccessAiInsights,
    featureAccess.canViewReorderReport,
  ]);

  useEffect(() => {
    (async () => {
      try {
        const resPermissionCategories = await fetchPermissionCategories();
        setRawPermissionCategories(resPermissionCategories);
      } catch {
        // do nothing
      }
    })();
  }, []);

  const handleActiveTabChange = useCallback(
    async (event: React.ChangeEvent<{}>, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );

  const classes = usePermissionsTabStyle();

  const renderTabLabel = useCallback(
    (permissionCategory: PermissionCategory) => {
      const state = getPermissionCategoryState(
        permissionCategory,
        permissionGroup
      );

      return (
        <span className={classes.tabWrapper}>
          <Checkbox
            className={'redesign large-checkbox'}
            checked={state !== PermissionCategoryState.NotChecked}
            color="primary"
            checkedIcon={
              state === PermissionCategoryState.PartiallyChecked ? (
                <CheckBoxOutlinedIcon />
              ) : undefined
            }
            onClick={(e: any) => {
              e.stopPropagation();
              if (state !== PermissionCategoryState.NotChecked) {
                removeCategoryPermissions(
                  permissionCategory,
                  setPermissionGroup
                );
                return;
              }
              addCategoryPermissions(permissionCategory, setPermissionGroup);
            }}
            disabled={!canEdit || permissionGroup.readOnly}
            data-qa={`permission-${permissionCategory.name}`}
          />
          <Typography variant="body2" color="textPrimary">
            {permissionCategory.name}
          </Typography>
        </span>
      );
    },
    [permissionGroup, canEdit, setPermissionGroup]
  );

  return (
    <Box display="flex" flex={1} className={classes.wrapper}>
      <Box display="flex" className={classes.sidebar}>
        <Tabs
          value={activeTab}
          onChange={handleActiveTabChange}
          textColor="primary"
          className={classes.tabs}
          orientation="vertical"
          sx={{ maxHeight: 'initial', minHeight: 'initial' }}
        >
          {permissionCategories.map((cat, catIndex) => (
            <Tab
              label={renderTabLabel(cat)}
              className={classes.tab}
              key={`cat_tab_${catIndex}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box className={classes.content}>
        {permissionCategories.map((permissionCategory, catIndex) => (
          <TabPanel
            value={activeTab}
            key={`cat_panel_${catIndex}`}
            index={catIndex}
            noSpacing
            overflow
            autoHeight
          >
            <Grid container spacing={0}>
              {permissionCategory.permissionAreas.map(
                (permissionArea, areaIndex) => (
                  <PermissionArea
                    key={`area_${catIndex}_${areaIndex}`}
                    permissionArea={permissionArea}
                    permissionGroup={permissionGroup}
                    setPermissionGroup={setPermissionGroup}
                  />
                )
              )}
            </Grid>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default memo(PermissionsTab);
